/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useAffinityRelatedProducts } from '@/features/pdpAffinity/api/getAffinityRelatedProducts';
import { useAffinityRelatedProductsACES } from '@/features/pdpAffinity/api/getAffinityRelatedProductsACES';
import { useAddAffinityItemsToCartMutation } from '@/features/pdpAffinity/api/postAddAffinityItemsToCart';
import { AffinityRelatedProductsBlock } from '@/features/pdpAffinity/components/AffinityRelatedProductsBlock';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { MINI_CART_TITLE_ID } from '@/features/miniCartFlyout';
import { selectOrderDetailsFromState } from '@/constants/selectors';
import { useSelector } from 'react-redux';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { Suspense, useState, useRef } from 'react';
import type { ReduxState } from '@/types';
import { Drawer } from '@/components/Drawer/Drawer';
import { TrapFocus } from '@/components/Dialog/Modal/TrapFocus';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import dynamic from 'next/dynamic';
import { useGetCertonaRecommendations } from '@/features/certona/api/useGetCertonaRecommendations';
import { certonaPageType } from '@/constants/certonaPageType';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';

const LazyMiniCartFlyoutComp = dynamic(
  () => import('@/features/miniCartFlyout').then((mod) => mod.MiniCartFlyoutComp),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

export type CMSCompleteTheJobProps = {
  shippingType: string;
  fulfillmentId: number;
  itemsToDisplay?: number;
};

export const CMSCompleteTheJob = ({
  shippingType,
  fulfillmentId,
  itemsToDisplay,
}: CMSCompleteTheJobProps) => {
  const { acesEnabled } = useAcesFlags();
  const useAffinity = acesEnabled ? useAffinityRelatedProductsACES : useAffinityRelatedProducts;
  const { data: affinityRelatedProducts } = useAffinity({
    itemsToDisplay,
  });
  const { data: headerData } = useHeaderData();
  const productDetailsQuery = useProductDetails();
  const productDetails = productDetailsQuery.isSuccess ? productDetailsQuery.data : null;
  const orderDetailsFromState = useSelector((state: ReduxState) =>
    selectOrderDetailsFromState(state),
  );
  const miniCartMap = headerData?.miniCartMap;
  const preferredVehicle = usePreferredVehicle();
  const storeNumber = headerData?.storeNumber;

  // Get SKU details for the main product
  const skuIds =
    productDetailsQuery.isSuccess && productDetails?.product?.itemId
      ? [productDetails.product.itemId]
      : null;
  const skuDetailsResult = useProductSkuDetails({ skuIds });
  const skuDetails = skuDetailsResult.isSuccess ? skuDetailsResult.data[0] : null;

  // Mini-cart flyout state
  const [showMiniCartFlyout, setShowMiniCartFlyout] = useState(false);
  const miniCartModalRef = useRef<HTMLDivElement | null>(null);

  // Fetch Certona recommendations for mini-cart (addtocart1_rr)
  const skuId = productDetails?.product?.itemId ?? '';
  const pageLevelCertonaRecommendations = useGetCertonaRecommendations(
    {
      certonaType: certonaPageType.cartAdd,
      skuIds: skuId,
      vehicleID: preferredVehicle?.partsProVehicleId?.toString(),
    },
    [skuId, storeNumber],
    !!skuId,
    false,
  );

  // Track when add to cart is triggered to open the flyout
  const addAffinityItemsToCartMutation = useAddAffinityItemsToCartMutation({
    onSuccess: () => {
      setShowMiniCartFlyout(true);
    },
  });

  const closeStoreModal = () => {
    // Reset any necessary state
  };

  const toggleMiniCartFlyout = () => {
    setShowMiniCartFlyout((prev) => !prev);
    closeStoreModal();
  };

  if (!affinityRelatedProducts?.length) {
    return null;
  }

  const productData = {
    productName: {
      productBrand: productDetails?.product?.brandName ?? '',
      productPart: productDetails?.product?.itemDescription ?? '',
      quickNote: undefined,
    },
    productId: productDetails?.product?.partGroupId,
    partTerminologyId: productDetails?.product?.ecommProductId,
    productAvailabilityInfo: {
      skuPricingAndAvailability: skuDetails?.skuPricingAndAvailability,
      dealsInfo: undefined,
      aZBadgesFlagVO: null,
    },
  };
  return (
    <>
      <AffinityRelatedProductsBlock
        affinityRelatedProducts={affinityRelatedProducts}
        shippingType={shippingType}
        fulfillmentId={fulfillmentId}
        addAffinityItemsToCartMutation={addAffinityItemsToCartMutation}
      />
      <Drawer
        data-testid="mini-cart-flyout"
        anchor="right"
        open={showMiniCartFlyout}
        onClose={toggleMiniCartFlyout}
        drawerRef={miniCartModalRef}
      >
        <TrapFocus open={showMiniCartFlyout}>
          <div role="dialog" aria-modal="true" aria-labelledby={MINI_CART_TITLE_ID}>
            <Suspense>
              {showMiniCartFlyout && productDetails && skuDetails && (
                <LazyMiniCartFlyoutComp
                  handleClose={toggleMiniCartFlyout}
                  isModalOpen={showMiniCartFlyout}
                  closeStoreModal={closeStoreModal}
                  successData={undefined}
                  miniCartData={miniCartMap}
                  image={productDetails.product?.itemImageUrl ?? ''}
                  productDetails={productData}
                  cartUrl={() => {}}
                  productTitle={productDetails.product?.itemDescription ?? ''}
                  pageName="miniCart"
                  orderDetailsFromState={orderDetailsFromState}
                  skuId={productDetails.product?.itemId}
                  parentRef={miniCartModalRef}
                  pageLevelCertonaRecommendations={pageLevelCertonaRecommendations}
                  hideCompleteTheJob={true}
                />
              )}
            </Suspense>
          </div>
        </TrapFocus>
      </Drawer>
    </>
  );
};
